import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo, fmtDate } from "@/lib/time";
import { Confirm } from "@/components/settings/Confirm";

const mobile = (d) => /iPhone|iPad|Android/.test(d);

const SessionRow = ({ s, onRevoke }) => {
  const I = mobile(s.device) ? Smartphone : Monitor;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-800 last:border-0 hover:bg-slate-800/30 transition-colors" data-testid={`session-row-${s.session_id}`}>
      <div className="w-9 h-9 rounded-md bg-slate-800 grid place-items-center shrink-0"><I size={16} className={s.current ? "text-cyan-300" : "text-slate-400"} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-100 flex items-center gap-2 flex-wrap">
          <span data-testid="session-device">{s.device}</span>
          {s.current && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300" data-testid="session-current-badge">This device</span>}
          {s.removed && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300" data-testid="session-removed-badge">Removed from team</span>}
        </p>
        <p className="text-xs text-slate-500 break-words">
          {s.ip ? `${s.ip} · ` : ""}signed in {fmtDate(s.created_at)} · active {timeAgo(s.last_seen_at)} · expires {fmtDate(s.expires_at)}
        </p>
      </div>
      <button data-testid="session-revoke-btn" onClick={onRevoke}
        className="h-8 px-2.5 rounded-md border border-slate-700 text-xs text-slate-300 flex items-center gap-1.5 hover:border-rose-500 hover:text-rose-300 transition-colors">
        <LogOut size={13} />Sign out
      </button>
    </div>
  );
};

export const SessionsPanel = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [target, setTarget] = useState(null);
  const owner = user?.role === "owner";
  const load = useCallback(() => api.get("/sessions").then(({ data }) => setList(data)).catch((x) => toast.error(errMsg(x))), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async () => {
    const s = target;
    setTarget(null);
    try {
      if (s === "others") { const { data } = await api.delete("/sessions/others"); toast.success(`Signed out ${data.revoked} other device${data.revoked !== 1 ? "s" : ""}`); return load(); }
      await api.delete(`/sessions/${s.session_id}`);
      if (s.current) { setUser(null); navigate("/"); return undefined; }
      toast.success(`Signed out ${s.device}${owner ? ` (${s.email})` : ""}`);
      return load();
    } catch (x) { toast.error(errMsg(x)); return undefined; }
  };
  if (!list) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>;
  const groups = list.reduce((m, s) => ({ ...m, [s.email]: [...(m[s.email] || []), s] }), {});
  const mine = list.filter((s) => s.email === user?.email && !s.current).length;
  return (
    <div className="space-y-6" data-testid="sessions-panel">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-heading text-base md:text-lg font-semibold">Active devices</h2>
          <p className="text-sm text-slate-400 mt-1">{owner ? "Every device signed in to this workspace, for you and your team." : "Devices signed in with your email."} Signing a device out ends that session immediately.</p>
        </div>
        <button data-testid="sessions-revoke-others-btn" onClick={() => setTarget("others")} disabled={!mine}
          className="h-9 px-3 rounded-md border border-slate-700 text-xs text-slate-200 flex items-center gap-1.5 hover:border-rose-500 hover:text-rose-300 transition-colors disabled:opacity-40">
          <LogOut size={14} />Sign out my other devices
        </button>
      </div>
      {Object.entries(groups).map(([email, ss]) => (
        <div key={email} className="rounded-lg border border-slate-800 bg-[#0d1220]" data-testid={`session-group-${email}`}>
          {owner && <div className="px-4 py-2 border-b border-slate-800 font-mono text-[11px] uppercase tracking-wider text-slate-400">{email}{email === user?.email ? " · you" : ""} · {ss.length} device{ss.length !== 1 ? "s" : ""}</div>}
          {ss.map((s) => <SessionRow key={s.session_id} s={s} onRevoke={() => setTarget(s)} />)}
        </div>
      ))}
      <Confirm open={!!target} testid="session-revoke-dialog" action="Sign out" onCancel={() => setTarget(null)} onConfirm={revoke}
        title={target === "others" ? "Sign out your other devices?" : "Sign out this device?"}
        body={target === "others" ? "All your other devices will be signed out immediately. This device stays signed in."
          : target?.current ? "You'll be signed out here and returned to the sign-in page." : `${target?.device} (${target?.email}) will be signed out immediately and will need a new email code to sign in again.`} />
    </div>
  );
};
